import { useState, useRef, useEffect, ReactNode } from "react";

interface UploadedFile extends File {
  fileOrgname?: string;
}

type FileType = "img-only" | "every";

type UseFileUploadProps = {
  uploadedFiles?: UploadedFile[] | File[];
  appendFile?: () => void;
  type?: FileType;
  isMulti?: boolean;
};

const useFileUpload = ({
  uploadedFiles = [],
  appendFile,
  type = "every",
  isMulti = true,
}: UseFileUploadProps) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<UploadedFile[]>([]);

  useEffect(() => {
    if (uploadedFiles) {
      setFiles(uploadedFiles);
    }
  }, [uploadedFiles.length]);

  const handleButtonClick = () => {
    fileRef.current?.click();
    const fileInput = document.getElementById(
      "file-upload"
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleChange = () => {
    if (!fileRef.current || !fileRef.current.files) return;

    const data = Array.from(fileRef.current.files) as UploadedFile[];
    const combinedFiles = [...data, ...files];

    const newUniqueFiles = combinedFiles.filter((item, index, self) =>
      item.fileOrgname
        ? index === self.findIndex((t) => t.fileOrgname === item.fileOrgname)
        : index ===
          self.findIndex((t) => t.name === item.name && t.size === item.size)
    );

    if (data.length > 0 && appendFile) {
      appendFile();
    }

    if (isMulti) {
      setFiles(newUniqueFiles);
    } else {
      setFiles([newUniqueFiles[0]]);
    }
  };

  const deleteFile = (file: UploadedFile) => {
    setFiles((prevFiles) =>
      prevFiles.filter((item) => item.name !== file.name)
    );
  };

  const deleteUpdatedFile = (file: UploadedFile) => {
    setFiles((prevFiles) =>
      prevFiles.filter((item) => item.fileOrgname !== file.fileOrgname)
    );
  };

  const UploadUI = ({ children }: { children?: ReactNode }) => (
    <label htmlFor="file-upload">
      <input
        className="form-control w-100 d-none"
        type="file"
        id="file-upload"
        ref={fileRef}
        onChange={handleChange}
      />
      {children}
    </label>
  );

  return {
    fileRef,
    files,
    handleButtonClick,
    handleChange,
    deleteFile,
    deleteUpdatedFile,
    UploadUI,
    setFiles,
  };
};

export default useFileUpload;
